import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useI18n } from "@/lib/i18n";
import { formatMoney, splitTip } from "@/lib/money";
import { getTipFeeInfo } from "@/lib/tips.functions";
import { fetchMyPenNames } from "@/lib/queries";
import { getWriterProfile, updateWriterProfile } from "@/lib/writer.functions";

/** A representative tip used only to illustrate the split. */
const EXAMPLE_TIP_MINOR = 500;

export const Route = createFileRoute("/_authenticated/studio/profile")({
  head: () => ({
    meta: [
      { title: "Writer profile — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Set the pen name, writer introduction and reader support options that appear on your public Quibblies author page.",
      },
      { property: "og:title", content: "Writer profile — Quibblies Creator Studio" },
      { property: "og:description", content: "Manage your public author identity." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WriterProfilePage,
});

function WriterProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const loadProfile = useServerFn(getWriterProfile);
  const saveProfile = useServerFn(updateWriterProfile);

  const { data: writerProfile, isLoading } = useQuery({
    queryKey: ["writer-profile"],
    queryFn: () => loadProfile(),
  });

  const { data: penNames = [] } = useQuery({
    queryKey: ["pen-names", user?.id],
    queryFn: () => fetchMyPenNames(user!.id),
    enabled: Boolean(user?.id),
  });

  const { language } = useI18n();
  const locale = language === "en" ? "en-GB" : language;
  const loadFees = useServerFn(getTipFeeInfo);
  const feeInfo = useQuery({
    queryKey: ["tip-fee-info"],
    queryFn: () => loadFees(),
    staleTime: 5 * 60 * 1000,
  });
  const feeBasisPoints = feeInfo.data?.feeBasisPoints ?? 500;
  const feePercent = feeBasisPoints / 100;
  const example = splitTip(EXAMPLE_TIP_MINOR, feeBasisPoints);

  const [intro, setIntro] = React.useState("");
  const [supportEnabled, setSupportEnabled] = React.useState(false);
  const [newPenName, setNewPenName] = React.useState("");

  React.useEffect(() => {
    if (writerProfile) {
      setIntro(writerProfile.intro ?? "");
      setSupportEnabled(writerProfile.support_enabled);
    }
  }, [writerProfile]);

  const save = useMutation({
    mutationFn: () => saveProfile({ data: { intro, supportEnabled } }),
    onSuccess: () => {
      toast.success("Writer profile saved.");
      void queryClient.invalidateQueries({ queryKey: ["writer-profile"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const addPenName = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("pen_names")
        .insert({ user_id: user!.id, name: newPenName.trim(), is_default: penNames.length === 0 });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewPenName("");
      toast.success("Pen name added.");
      void queryClient.invalidateQueries({ queryKey: ["pen-names"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading) return <Skeleton className="h-72 w-full rounded-2xl" />;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">
        <h2 className="font-display text-xl text-foreground">Writer introduction</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Shown on your public author page alongside your writing.
        </p>
        <Textarea
          className="mt-4"
          rows={5}
          value={intro}
          maxLength={1000}
          onChange={(event) => setIntro(event.target.value)}
          placeholder="Tell readers what kind of writing you share here."
        />
      </section>

      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">
        <h2 className="font-display text-xl text-foreground">Pen names</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Publish under a name of your choosing. Your email, date of birth and other private account
          details are never shown publicly. Administrators can still identify the account behind a
          publication when moderation requires it.
        </p>
        <ul className="mt-4 space-y-2">
          {penNames.map((pen) => (
            <li
              key={pen.id}
              className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2 text-sm text-foreground"
            >
              {pen.name}
              {pen.is_default ? (
                <span className="text-xs uppercase tracking-wide text-primary">Default</span>
              ) : null}
            </li>
          ))}
          {penNames.length === 0 ? (
            <li className="text-sm text-muted-foreground">No pen names yet.</li>
          ) : null}
        </ul>
        <div className="mt-4 flex gap-2">
          <div className="flex-1">
            <Label htmlFor="new-pen-name" className="sr-only">
              New pen name
            </Label>
            <Input
              id="new-pen-name"
              value={newPenName}
              maxLength={60}
              onChange={(event) => setNewPenName(event.target.value)}
              placeholder="Add another pen name"
            />
          </div>
          <Button
            variant="soft"
            disabled={!newPenName.trim() || addPenName.isPending}
            onClick={() => addPenName.mutate()}
          >
            Add
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl text-foreground">Enable tipping</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Off by default. When this is on, readers see a <strong>Tip Writer</strong> button on
              your author page and on each of your publications. Nothing you write is ever
              paywalled, and turning this off hides the button again straight away.
            </p>
          </div>
          <Switch
            checked={supportEnabled}
            onCheckedChange={setSupportEnabled}
            aria-label="Enable tipping"
          />
        </div>

        <div className="mt-4 rounded-xl border border-border/60 bg-surface/50 p-4 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            How a tip is split
          </p>
          <dl className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Example tip</dt>
              <dd className="text-foreground">
                {formatMoney(EXAMPLE_TIP_MINOR, "GBP", locale)}
              </dd>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <dt>Quibblies platform fee ({feePercent}%)</dt>
              <dd>-{formatMoney(example.fee, "GBP", locale)}</dd>
            </div>
            <div className="flex items-center justify-between border-t border-border/60 pt-2">
              <dt className="text-foreground">You receive</dt>
              <dd className="font-display text-lg text-primary-glow">
                {formatMoney(example.net, "GBP", locale)}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            The fee covers payment processing and running the platform.
            {feeInfo.data?.liveMode
              ? ""
              : " Payments are not live yet, so tips are recorded as pending until the payment provider is connected."}
          </p>
        </div>
      </section>

      <Button variant="hero" size="pill" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? "Saving…" : "Save writer profile"}
      </Button>
    </div>
  );
}
